import React from "react";
import { Button, Dropdown, Space, Badge } from "antd";
import { useNavigate, useLocation } from "react-router-dom";
import {
  HomeOutlined,
  CalculatorOutlined,
  ContainerOutlined,
  TeamOutlined,
  CheckSquareOutlined,
  UserOutlined,
  GlobalOutlined,
  LineChartOutlined,
  DownOutlined,
  AppstoreOutlined,
  FileTextOutlined,
} from "@ant-design/icons";
import { trackFeatureUsage } from "../lib/posthog";
import styled from "@emotion/styled";

const NavContainer = styled.div`
  margin-bottom: 24px;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
  background: #f5f5f5;
  padding: 12px;
  border-radius: 4px;

  @media (max-width: 768px) {
    .desktop-button {
      display: none;
    }
    .mobile-dropdown {
      display: block;
    }
  }

  @media (min-width: 769px) {
    .mobile-dropdown {
      display: none;
    }
  }
`;

const NavButton = styled(Button)`
  border-radius: 4px;
  border: ${(props) =>
    props["data-active"] === "true"
      ? "1px solid #1890ff"
      : "1px solid transparent"};
  background: ${(props) =>
    props["data-active"] === "true" ? "#e6f7ff" : "transparent"};
  color: ${(props) =>
    props["data-active"] === "true" ? "#1890ff" : "rgba(0, 0, 0, 0.65)"};

  &:hover {
    color: #1890ff;
    border-color: #1890ff;
    background: #e6f7ff;
  }

  .anticon {
    margin-right: 8px;
  }
`;

const WipBadge = styled(Badge)`
  .ant-badge-count {
    background-color: #faad14;
    color: #fff;
    box-shadow: none;
    font-weight: normal;
    font-size: 10px;
    height: 18px;
    line-height: 18px;
  }
`;

export const AdminNavBar: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const currentPath = location.pathname;

  const adminSections = [
    {
      title: "Dashboard",
      icon: <HomeOutlined />,
      path: "/admin",
      feature: "dashboard",
    },
    {
      title: "Bookings",
      icon: <ContainerOutlined />,
      path: "/admin/bookings",
      feature: "bookings",
    },
    {
      title: "Containers",
      icon: <ContainerOutlined />,
      path: "/admin/containers",
      feature: "containers",
    },
    {
      title: "Tariffs",
      icon: <ContainerOutlined />,
      path: "/admin/tariffs",
      feature: "tariffs",
    },
    {
      title: "Companies",
      icon: <TeamOutlined />,
      path: "/admin/companies",
      feature: "companies",
    },
    {
      title: "Tasks",
      icon: <CheckSquareOutlined />,
      path: "/admin/tasks",
      feature: "tasks",
    },
    {
      title: "Users",
      icon: <UserOutlined />,
      path: "/admin/users",
      feature: "users",
    },
    {
      title: "Ports",
      icon: <GlobalOutlined />,
      path: "/admin/ports",
      feature: "ports",
    },
    {
      title: "Invoice Settings",
      icon: <CalculatorOutlined />,
      path: "/admin/pricing",
      feature: "pricing",
      wip: true,
    },
    {
      title: "Reports",
      icon: <LineChartOutlined />,
      path: "/admin/reports",
      feature: "reports",
      wip: true,
    },
    {
      title: "Invoices",
      icon: <FileTextOutlined />,
      path: "/admin/invoices",
      feature: "invoices",
      wip: true,
    },
  ];

  const handleNavigate = (path: string, feature: string) => {
    navigate(path);
    trackFeatureUsage(`admin_${feature}`);
  };

  const isActive = (path: string) => {
    if (path === "/admin") {
      return currentPath === "/admin";
    }
    return currentPath.startsWith(path);
  };

  const menuItems = adminSections.map((section) => ({
    key: section.path,
    icon: section.icon,
    label: section.wip ? `${section.title} (WIP)` : section.title,
    onClick: () => handleNavigate(section.path, section.feature),
  }));

  return (
    <NavContainer>
      <Space size="small" wrap>
        {adminSections.map((section) => (
          <NavButton
            key={section.path}
            data-active={isActive(section.path).toString()}
            icon={section.icon}
            onClick={() => handleNavigate(section.path, section.feature)}
            className="desktop-button"
          >
            {section.wip ? (
              <WipBadge count="WIP" size="small">
                {section.title}
              </WipBadge>
            ) : (
              section.title
            )}
          </NavButton>
        ))}
      </Space>

      <Dropdown
        menu={{ items: menuItems }}
        className="mobile-dropdown"
        trigger={["click"]}
      >
        <Button icon={<AppstoreOutlined />}>
          Admin Pages <DownOutlined />
        </Button>
      </Dropdown>
    </NavContainer>
  );
};
